import {
  Network,
  BarChart3,
  Route,
  GitMerge,
  Radio,
  Target,
  Building2,
} from "lucide-react";
import React from "react";
import { PageLayout } from "../components";
import ProductOverview from "../components/product/ProductOverview";
import { useTranslation } from "../contexts/LanguageContext";
import { openLeadflowSSO } from "../shared/leadflowSso";

/**
 * Página comercial do Virtus LeadFlow (plataforma separada de captação,
 * mapeamento e distribuição de leads pra CRMs). O SSO é preservado 100%
 * como já existia — `openLeadflowSSO()` não foi alterado, só passou a ser
 * chamado a partir do CTA da nova estrutura de apresentação do produto.
 */
const Leadflow: React.FC = () => {
  const { t } = useTranslation();

  return (
    <PageLayout
      title={t("Virtus LeadFlow")}
      subtitle={t(
        "Captação, mapeamento e distribuição de leads para seus CRMs.",
      )}
      containerSize="full"
      showContainer={true}
      padding="none"
      className="w-full px-2! py-2!"
    >
      <ProductOverview
        icon={Network}
        productName={t("Virtus LeadFlow")}
        tagline={t(
          "Rastreie a jornada completa do lead — da campanha até o CRM — em uma única plataforma.",
        )}
        description={t(
          "O Virtus LeadFlow centraliza os leads gerados em Meta Ads, Google Ads e Landing Pages, acompanha cada etapa da jornada em tempo real e distribui automaticamente para o seu CRM conforme as regras que você configurar. Hoje com integração nativa a RD Station e CVCRM — outros CRMs podem ser integrados conforme a necessidade do cliente. O acesso é feito com o mesmo login do Social Manager, sem precisar entrar de novo.",
        )}
        ctaLabel={t("Abrir Virtus LeadFlow")}
        onCta={openLeadflowSSO}
        benefitsTitle={t("Por que usar o LeadFlow")}
        benefitsSubtitle={t("O que ele resolve no seu fluxo de leads")}
        benefits={[
          {
            icon: Route,
            title: t("Jornada rastreável"),
            description: t(
              "Acompanha cada interação do lead desde a origem (Meta Ads, Google Ads, Landing Page) até o destino final.",
            ),
          },
          {
            icon: GitMerge,
            title: t("Regras de distribuição"),
            description: t(
              "Define como cada lead é encaminhado automaticamente para o CRM certo, sem trabalho manual.",
            ),
          },
          {
            icon: BarChart3,
            title: t("Performance de campanhas"),
            description: t(
              "Analisa o retorno das campanhas Meta Ads que geraram os leads, com ranking e métricas granulares.",
            ),
          },
        ]}
        featuresTitle={t("Principais recursos")}
        featuresSubtitle={t("O que você encontra dentro do LeadFlow")}
        features={[
          {
            icon: BarChart3,
            title: t("Meta Insights"),
            description: t(
              "KPIs, ranking e desempenho granular das campanhas Meta Ads que originaram os leads.",
            ),
          },
          {
            icon: GitMerge,
            title: t("Mapeamento de Leads"),
            description: t(
              "Regras de distribuição e integrações configuráveis com CRMs — hoje RD Station e CVCRM, com espaço para outros conforme a necessidade do cliente.",
            ),
          },
          {
            icon: Route,
            title: t("Jornada do Lead"),
            description: t(
              "Histórico completo de cada lead, com origem identificada (Meta Ads, Google Ads, RD Station, Landing Page ou CVCRM).",
            ),
          },
          {
            icon: Radio,
            title: t("Acesso único (SSO)"),
            description: t(
              "Entra direto com o login do Social Manager, sem cadastro separado.",
            ),
          },
        ]}
        flowTitle={t("Como ele se encaixa no seu fluxo")}
        flowSubtitle={t("Da campanha até o seu CRM")}
        flow={[
          { icon: Target, label: t("Meta Ads / Google Ads") },
          { icon: Network, label: t("Virtus LeadFlow") },
          { icon: Building2, label: t("CRM") },
        ]}
      />
    </PageLayout>
  );
};

export default Leadflow;
